import React, { useMemo } from 'react';
import { MapContainer, TileLayer, CircleMarker, Tooltip } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { useTheme } from '../theme';
import { useLang } from '../i18n';

/**
 * Interactive OpenStreetMap (Leaflet) map for sign-in locations.
 *
 * Markers are placed at approximate country centroids (there is no per-login
 * lat/lon in the data). Each marker's radius scales with the number of
 * sign-ins from that country and a tooltip shows the country and count.
 * Countries never seen before ("new") are highlighted in amber.
 *
 * The tile layer automatically switches between a light and a dark basemap to
 * match the site's current light/dark theme.
 *
 * Accepts two shapes of `items`:
 *   - Account history rows: each row is a single sign-in (we count 1 each).
 *   - Admin aggregates: rows that already carry a numeric `count`.
 * An optional `is_new` flag on any row marks that country as suspicious/new.
 */

// ISO-3166 alpha-2 -> approximate country centroid [lat, lon].
const COUNTRY_CENTROIDS = {
  DE: [51.2, 10.4], AT: [47.6, 14.1], CH: [46.8, 8.2], FR: [46.6, 2.3], GB: [54.0, -2.0],
  IE: [53.2, -8.0], ES: [40.2, -3.7], PT: [39.6, -8.0], IT: [42.8, 12.8], NL: [52.1, 5.3],
  BE: [50.6, 4.6], LU: [49.8, 6.1], DK: [56.0, 9.5], SE: [62.0, 15.0], NO: [61.0, 8.5],
  FI: [64.0, 26.0], PL: [52.0, 19.1], CZ: [49.8, 15.5], SK: [48.7, 19.7], HU: [47.2, 19.4],
  RO: [45.9, 24.9], BG: [42.7, 25.5], GR: [39.1, 22.0], HR: [45.1, 15.5], SI: [46.1, 14.8],
  RS: [44.0, 21.0], UA: [49.0, 32.0], RU: [61.5, 90.0], TR: [39.0, 35.2], IS: [64.9, -19.0],
  EE: [58.7, 25.5], LV: [56.9, 24.6], LT: [55.2, 23.9],
  US: [39.5, -98.3], CA: [56.1, -106.3], MX: [23.6, -102.5], BR: [-10.3, -53.2],
  AR: [-38.4, -63.6], CL: [-35.7, -71.5], CO: [4.6, -74.3], PE: [-9.2, -75.0],
  IN: [22.0, 79.0], CN: [35.9, 104.2], JP: [36.2, 138.3], KR: [36.5, 127.8],
  ID: [-2.5, 118.0], TH: [15.9, 101.0], VN: [14.1, 108.3], PH: [12.9, 121.8],
  SG: [1.35, 103.8], MY: [4.2, 101.9], AE: [23.4, 53.8], SA: [23.9, 45.1], IL: [31.0, 34.9],
  ZA: [-30.6, 22.9], EG: [26.8, 30.8], NG: [9.1, 8.7], KE: [-0.0, 37.9], MA: [31.8, -7.1],
  AU: [-25.3, 133.8], NZ: [-41.0, 174.0],
};

// Amber for "new/suspicious" country markers (matches the amber-500 used in the
// admin lists). Leaflet needs concrete colour strings, so CSS vars / hex here.
const NEW_COLOR = '#f59e0b';
const NORMAL_COLOR = 'hsl(var(--primary))';

const LIGHT_TILES = {
  url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
  attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
};
const DARK_TILES = {
  url: 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png',
  attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>',
};

export const LoginMap = ({ items = [], height = 240, en: enProp }) => {
  const { resolved } = useTheme();
  const { lang } = useLang();
  const en = typeof enProp === 'boolean' ? enProp : lang === 'en';
  const tr = (de, eng) => (en ? eng : de);
  const isDark = resolved === 'dark';
  const tiles = isDark ? DARK_TILES : LIGHT_TILES;

  const pins = useMemo(() => {
    const byCode = {};
    items.forEach((it) => {
      const code = (it.country_code || '').toUpperCase();
      if (!code || !COUNTRY_CENTROIDS[code]) return;
      const inc = Number.isFinite(it.count) ? it.count : 1;
      if (!byCode[code]) byCode[code] = { code, country: it.country || code, count: 0, is_new: false };
      byCode[code].count += inc;
      if (it.is_new) byCode[code].is_new = true;
    });
    return Object.values(byCode);
  }, [items]);

  const maxCount = Math.max(1, ...pins.map((p) => p.count));
  const hasNew = pins.some((p) => p.is_new);

  if (pins.length === 0) return null;

  return (
    <div data-testid="login-map">
      <div className="overflow-hidden rounded-lg border" style={{ height }}>
        <MapContainer
          center={[30, 10]}
          zoom={1}
          minZoom={1}
          maxZoom={10}
          scrollWheelZoom={false}
          worldCopyJump
          style={{ height: '100%', width: '100%', background: isDark ? 'hsl(var(--muted))' : 'hsl(var(--muted))' }}
          aria-label="World map of recent sign-in locations"
        >
          <TileLayer key={resolved} attribution={tiles.attribution} url={tiles.url} />
          {pins.map((p) => {
            const [lat, lon] = COUNTRY_CENTROIDS[p.code];
            // Scale radius between 6 and 20 px by relative share.
            const radius = 6 + Math.round((p.count / maxCount) * 14);
            const color = p.is_new ? NEW_COLOR : NORMAL_COLOR;
            return (
              <CircleMarker
                key={p.code}
                center={[lat, lon]}
                radius={radius}
                pathOptions={{
                  color,
                  fillColor: color,
                  fillOpacity: p.is_new ? 0.55 : 0.45,
                  weight: p.is_new ? 3 : 2,
                }}
                data-testid={`login-map-pin-${p.code}`}
              >
                <Tooltip direction="top" offset={[0, -4]} opacity={1}>
                  <span style={{ fontWeight: 600 }}>{p.country}</span>
                  {` — ${p.count} ${tr('Anmeldung', 'sign-in')}${p.count > 1 ? (en ? 's' : 'en') : ''}`}
                  {p.is_new ? ' ⚠' : ''}
                </Tooltip>
              </CircleMarker>
            );
          })}
        </MapContainer>
      </div>
      {hasNew ? (
        <div className="mt-1.5 flex items-center gap-1.5 text-[11px]" style={{ color: NEW_COLOR }} data-testid="login-map-new-legend">
          <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ background: NEW_COLOR }} />
          <span>{tr('Neuer / bisher unbekannter Ort', 'New / previously unseen location')}</span>
        </div>
      ) : null}
    </div>
  );
};

export default LoginMap;
